import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Sparkles, Wallet, AlertTriangle, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function CreditsPage() {
  const [w, setW] = useState(null);
  const [tx, setTx] = useState([]);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/saas/wallet"),
        api.get("/saas/transactions", { params: { limit: 100 } }),
      ]);
      setW(a.data); setTx(b.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!w) return null;

  const bal = w.balance || 0;
  const used = w.used_this_month || 0;
  const total = bal + used;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const low = bal < 100;
  const critical = bal < 20;

  return (
    <div>
      <PageHeader title="Kredilerim" subtitle="AI ve premium özellik kullanım bakiyeniz" />
      <div className="p-6 max-w-4xl mx-auto space-y-4">
        <Card className="p-5 border-blue-800 bg-gradient-to-br from-blue-950/60 to-slate-950/60" data-testid="credits-summary-card">
          <div className="flex items-start gap-4">
            <Wallet className="w-8 h-8 text-blue-400 mt-1" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-blue-300 font-mono">Mevcut Kredi</div>
              <div className="text-4xl font-bold text-white font-mono tabular-nums mt-1" data-testid="credits-balance">{bal.toLocaleString("tr-TR")}</div>
              <div className="text-xs text-slate-400 mt-2">
                Bu ay kullanılan: <b className="text-slate-200">{used}</b> kredi ·
                Toplam alınan: <b className="text-slate-200">{total}</b>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2 mt-3 overflow-hidden">
                <div className={`h-2 ${critical ? "bg-rose-500" : low ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${100 - pct}%` }} />
              </div>
              {critical && (
                <div className="flex items-center gap-2 mt-3 text-rose-300 text-sm" data-testid="credits-warning">
                  <AlertTriangle className="w-4 h-4" /> AI krediniz bitmek üzere! Premium özellikler yakında kullanılamayabilir.
                </div>
              )}
              {low && !critical && (
                <div className="flex items-center gap-2 mt-3 text-amber-300 text-sm" data-testid="credits-warning">
                  <TrendingDown className="w-4 h-4" /> AI krediniz azalıyor. Kredi ekletmeyi düşünebilirsiniz.
                </div>
              )}
            </div>
            <Button className="bg-blue-600 hover:bg-blue-500 rounded-sm" disabled data-testid="credits-topup-btn"
              title="Online kredi satın alma yakında aktif olacaktır">
              <CreditCard className="w-4 h-4 mr-1" /> + Kredi Yükle
            </Button>
          </div>
          <div className="mt-3 text-[10px] text-slate-500">
            Online kredi satın alma yakında aktif olacaktır. Şimdilik yöneticinizle görüşün.
          </div>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Paket Kredisi" value={w.package_balance || 0} />
          <Kpi label="Satın Alınan" value={w.purchased_balance || 0} />
          <Kpi label="Promosyon" value={w.promo_balance || 0} />
          <Kpi label="Bu Ay Kullanılan" value={used} tone="amber" />
        </div>

        <Card className="bg-slate-950/60 border-slate-800 overflow-x-auto">
          <div className="p-3 text-xs uppercase tracking-widest text-slate-400 font-mono border-b border-slate-800 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-blue-400" /> Kredi Hareketleri
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-900">
              <tr>
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">İşlem</th>
                <th className="text-left px-3 py-2">Özellik</th>
                <th className="text-right px-3 py-2">Tutar</th>
                <th className="text-right px-3 py-2">Bakiye</th>
                <th className="text-left px-3 py-2">Not</th>
              </tr>
            </thead>
            <tbody>
              {tx.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-6">Henüz hareket yok</td></tr>}
              {tx.map((t) => (
                <tr key={t.id} className="border-b border-slate-900" data-testid={`tx-${t.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-400 font-mono">{new Date(t.created_at).toLocaleString("tr-TR")}</td>
                  <td className="px-3 py-2">
                    <Badge className={`rounded-sm text-[9px] uppercase ${t.kind === "debit" ? "bg-rose-950 text-rose-300 border-rose-800" : "bg-emerald-950 text-emerald-300 border-emerald-800"}`}>
                      {t.kind === "debit" ? "Harcama" : "Yükleme"} · {t.source}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-300 text-xs">{t.feature || "—"}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${t.kind === "debit" ? "text-rose-300" : "text-emerald-300"}`}>
                    {t.kind === "debit" ? "-" : "+"}{t.amount}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-200">{t.balance_after}</td>
                  <td className="px-3 py-2 text-xs text-slate-400 truncate max-w-xs">{t.notes || t.user_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const t = tone === "amber" ? "text-amber-300 border-amber-900 bg-amber-950/40" : "text-slate-200 border-slate-800 bg-slate-950/60";
  return (
    <div className={`border rounded-sm p-3 ${t}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="mt-1 text-xl font-bold font-mono tabular-nums">{Number(value || 0).toLocaleString("tr-TR")}</div>
    </div>
  );
}
